import { useState, useEffect } from 'react';

/**
 * Minimal structural shape of the parts of `navigator` we read. Declared inline
 * so we don't have to augment the global `Navigator` type for the experimental
 * `userAgentData` Client-Hints API (Chromium-only).
 */
export interface MobileNav {
  userAgentData?: { mobile?: boolean };
  userAgent?: string;
}

/**
 * Is this a phone-class device?
 *
 * We key off the **User-Agent signal** — `navigator.userAgentData.mobile` when
 * the browser exposes Client Hints (Chromium), otherwise the W3C-recommended
 * `/Mobi/` UA token — NOT the viewport width.
 *
 * Why UA and not width: this is exactly what makes the browser's native
 * **"Request Desktop Site"** work and *persist*. That feature swaps the UA to a
 * desktop one (and flips `userAgentData.mobile` to `false`), and Chrome/Safari
 * remember the choice **per-site**, re-applying it on every reload and across
 * SPA navigation. A UA-driven check therefore flips to the desktop layout and
 * stays there. The old `innerWidth < 640` check ignored that signal: with a
 * `width=device-width` viewport the innerWidth usually stays narrow on a phone
 * even in desktop mode, so the mobile layout never lifted.
 *
 * Bonus: tablets/iPads report non-mobile (modern iPadOS sends a desktop UA, and
 * `/Mobi/` is absent on tablets), so they correctly get the desktop experience —
 * with graphics scaled independently by the GPU quality tier, not this flag.
 */
export function detectIsMobile(nav?: MobileNav): boolean {
  const n: MobileNav | undefined =
    nav ??
    (typeof navigator !== 'undefined' ? (navigator as MobileNav) : undefined);
  if (!n) return false; // non-browser (SSR / unit test) — default to desktop
  const uad = n.userAgentData;
  if (uad && typeof uad.mobile === 'boolean') return uad.mobile;
  return /Mobi/i.test(n.userAgent ?? '');
}

/**
 * React hook wrapper around {@link detectIsMobile}. Re-evaluates on focus and
 * visibility changes so a same-tab desktop-mode switch is picked up even when it
 * doesn't trigger a hard reload (it usually does).
 */
export function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState<boolean>(() => detectIsMobile());

  useEffect(() => {
    const check = () => setIsMobile(detectIsMobile());
    window.addEventListener('resize', check);
    window.addEventListener('focus', check);
    document.addEventListener('visibilitychange', check);
    return () => {
      window.removeEventListener('resize', check);
      window.removeEventListener('focus', check);
      document.removeEventListener('visibilitychange', check);
    };
  }, []);

  return isMobile;
}
